import React from "react"
import cx from "classnames"
import { VisuallyHidden } from "../visually-hidden"
import { useRender } from "@base-ui/react"
import { KitLogo } from "../kit-logo"
import { matchesPath } from "./navigation-utils"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationLogoLinkProps = {
  /** Visually hidden text for the logo link */
  accessibleName?: string
  /** The href for the logo link */
  pathPrefix: string
  render?: useRender.RenderProp
}

// =============================================================================
// Styles
// =============================================================================

const navigationLogoLinkStyles = [
  "rounded-[10px] p-2",
  "text-gray-900 hover:text-gray-800",
  "outline-2 outline-transparent focus-visible:outline-gray-900",
  "transition-colors"
]

// =============================================================================
// Component
// =============================================================================

/**
 * The Kit navigation logo link.
 *
 * Renders a link to the home page with the Kit logo and visually hidden text.
 *
 * Pass a `render` prop to inject your own link component (e.g. Inertia `Link`)
 */
export function NavigationLogoLink({
  accessibleName = "Kit",
  pathPrefix,
  render
}: NavigationLogoLinkProps) {
  const { currentPath } = useNavigationContext()
  const isActive = matchesPath(currentPath, pathPrefix)

  return useRender({
    defaultTagName: "a",
    render,
    props: {
      href: pathPrefix,
      className: cx(navigationLogoLinkStyles),
      children: (
        <>
          <KitLogo className="h-6 w-auto" />
          <VisuallyHidden>{accessibleName}</VisuallyHidden>
        </>
      ),
      "aria-current": isActive ? ("page" as const) : undefined,
      "data-slot": "navigation-logo-link"
    }
  })
}

NavigationLogoLink.displayName = "Navigation.LogoLink"
